import {
  Divider,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { UngroupedDocumentsSection } from '../components/UngroupedDocumentsSection';
import { groupIngestionDocumentsByFolder } from '../utils/ingestionDocuments.utils';
import { FolderDocumentsSection } from './FolderDocumentsSection';

interface FolderGroupedDocumentLayoutProps {
  documents: ExternalIngestionDocumentFragment[];
  selectedDocumentId: string | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  validationData: ExternalValidationReportDataFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  selectFirstDocument?: boolean;
  onDocumentSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
}

export const FolderGroupedDocumentLayout: FC<
  FolderGroupedDocumentLayoutProps
> = ({
  documents,
  selectedDocumentId,
  selectedDocument,
  validationData,
  documentReviews,
  submittedDocumentReviews,
  hiddenDocumentValidationReviews,
  selectFirstDocument = true,
  onDocumentSelect,
}) => {
  const [tabIndex, setTabIndex] = useState(0);

  const groupedDocuments = useMemo(() => {
    return groupIngestionDocumentsByFolder(
      documents,
      validationData,
      submittedDocumentReviews
    );
  }, [documents, validationData, submittedDocumentReviews]);

  // Default to "Needs Review" tab if there are needs review documents
  useEffect(() => {
    if (groupedDocuments.needsReviewDocuments.length > 0) {
      setTabIndex(0);
    } else {
      setTabIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (documents.length > 0 && !selectedDocumentId) {
      // First prioritize needs review documents
      const firstDocument =
        groupedDocuments.needsReviewDocuments[0] ||
        // Then look through folder groups for the first active document
        groupedDocuments.folderGroups.find(
          (group) => group.activeDocuments.length > 0
        )?.activeDocuments[0] ||
        // Then look through folder groups for the first archived document
        groupedDocuments.folderGroups.find(
          (group) => group.archivedDocuments.length > 0
        )?.archivedDocuments[0] ||
        // Finally fallback to any document
        documents[0];

      if (firstDocument && selectFirstDocument) {
        onDocumentSelect(firstDocument);
      }
    }
  }, [
    documents,
    selectedDocumentId,
    onDocumentSelect,
    groupedDocuments,
    selectFirstDocument,
  ]);

  const handleDocumentSelect = useCallback(
    (
      document: ExternalIngestionDocumentFragment,
      contractDocumentId?: string
    ) => {
      onDocumentSelect(document, contractDocumentId);

      if (contractDocumentId && document.contractDocumentPages) {
        const pageInfo = document.contractDocumentPages[contractDocumentId] as
          | { startPage?: number; endPage?: number }
          | undefined;

        if (pageInfo?.startPage) {
          setTimeout(() => {
            const pageElement = window.document.querySelector(
              `div[data-page-number="${pageInfo.startPage}"]`
            );

            if (pageElement) {
              pageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }, 100);
        }
      }
    },
    [onDocumentSelect]
  );

  return (
    <>
      {documents.length === 0 ? (
        <Text color="compliance.text.muted">No documents found</Text>
      ) : (
        <Tabs
          index={tabIndex}
          size="sm"
          variant="soft-rounded"
          onChange={setTabIndex}
        >
          <TabList mb={4}>
            <Tab
              _hover={{ bg: 'compliance.tab.hover.bg' }}
              _selected={{
                bg: 'compliance.tab.selected.bg',
                color: 'compliance.tab.selected.text',
              }}
              bg="compliance.tab.bg"
              color="compliance.tab.text"
            >
              Needs Review ({groupedDocuments.needsReviewDocuments.length})
            </Tab>
            <Tab
              _hover={{ bg: 'compliance.tab.hover.bg' }}
              _selected={{
                bg: 'compliance.tab.selected.bg',
                color: 'compliance.tab.selected.text',
              }}
              bg="compliance.tab.bg"
              color="compliance.tab.text"
            >
              All Documents
            </Tab>
          </TabList>

          <TabPanels>
            {/* Needs Review Tab - Using folder-style layout */}
            <TabPanel pt={0} px={0}>
              {groupedDocuments.needsReviewDocuments.length > 0 ? (
                <UngroupedDocumentsSection
                  defaultCollapsed={false}
                  documentReviews={documentReviews}
                  documents={groupedDocuments.needsReviewDocuments}
                  hiddenDocumentValidationReviews={
                    hiddenDocumentValidationReviews
                  }
                  selectedDocument={selectedDocument}
                  showSubtitle={true}
                  submittedDocumentReviews={submittedDocumentReviews}
                  subtitleType="folder"
                  validationData={validationData}
                  onDocumentSelect={handleDocumentSelect}
                />
              ) : (
                <Text color="compliance.text.muted">
                  No documents need review
                </Text>
              )}
            </TabPanel>

            {/* All Documents Tab */}
            <TabPanel pt={0} px={0}>
              {groupedDocuments.folderGroups.length > 0 ? (
                groupedDocuments.folderGroups.map((folderGroup, i) => (
                  <Flex key={folderGroup.folderId} direction="column">
                    <FolderDocumentsSection
                      activeDocuments={folderGroup.activeDocuments}
                      archivedDocuments={folderGroup.archivedDocuments}
                      defaultCollapsed={false}
                      documentReviews={documentReviews}
                      folderName={folderGroup.folderName}
                      hiddenDocumentValidationReviews={
                        hiddenDocumentValidationReviews
                      }
                      selectedDocument={selectedDocument}
                      submittedDocumentReviews={submittedDocumentReviews}
                      validationData={validationData}
                      onDocumentSelect={handleDocumentSelect}
                    />
                    {i < groupedDocuments.folderGroups.length - 1 && (
                      <Divider my={3} opacity={1} />
                    )}
                  </Flex>
                ))
              ) : (
                <Text color="compliance.text.muted">No documents found</Text>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </>
  );
};
